import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, Cpu, BarChart3, Film, Upload, ArrowRight, Loader2, Sparkles, Video, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  { value: 'ai_image', label: 'AI Image', icon: Sparkles, cost: 5, desc: 'FLUX.1 — text-to-image generation' },
  { value: 'ai_video', label: 'AI Video', icon: Video, cost: 35, desc: 'CogVideoX-5b — text-to-video generation' },
  { value: '3d_render', label: '3D Render', icon: Cpu, cost: 15, desc: 'Photorealistic ray-traced output' },
  { value: 'data_viz', label: 'Data Viz', icon: BarChart3, cost: 3, desc: 'Dataset analysis and charts' },
  { value: 'video_render', label: 'Video Encode', icon: Film, cost: 25, desc: 'Encode, transcode, composite' },
];

const PRESET_SERVICES = new Set(['ai_image', 'ai_video']);

export default function SubmitJob() {
  const navigate = useNavigate();
  const [serviceType, setServiceType] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [imagePresets, setImagePresets] = useState([]);
  const [videoPresets, setVideoPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const selectedService = SERVICE_OPTIONS.find((s) => s.value === serviceType);
  const showPresets = PRESET_SERVICES.has(serviceType);
  const currentPresets = serviceType === 'ai_video' ? videoPresets : imagePresets;
  const categories = ['All', ...Array.from(new Set(currentPresets.map((p) => p.category)))];
  const filteredPresets = activeCategory === 'All' ? currentPresets : currentPresets.filter((p) => p.category === activeCategory);

  useEffect(() => {
    axios.get(`${API}/presets`).then((r) => {
      setImagePresets(r.data.image || []);
      setVideoPresets(r.data.video || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setSelectedPreset(null);
    setActiveCategory('All');
  }, [serviceType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!serviceType || !title) { toast.error('Select a service and enter a title.'); return; }
    setLoading(true);
    try {
      let filePath = null;
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        filePath = uploadRes.data.path;
      }

      // Compose prompt with preset suffix
      let finalDescription = description;
      const config = filePath ? { input_file: filePath } : {};
      if (selectedPreset) {
        finalDescription = description ? `${description}, ${selectedPreset.suffix}` : selectedPreset.suffix;
        config.preset_id = selectedPreset.id;
        config.preset_name = selectedPreset.name;
        if (selectedPreset.negative) config.negative_prompt = selectedPreset.negative;
        if (selectedPreset.params) Object.assign(config, selectedPreset.params);
      }

      const res = await axios.post(`${API}/jobs`, {
        service_type: serviceType,
        title,
        description: finalDescription,
        config,
      }, { withCredentials: true });
      toast.success('Job submitted.');
      navigate(`/dashboard/jobs/${res.data.job_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit job.');
    } finally { setLoading(false); }
  };

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); };

  return (
    <div className="max-w-3xl space-y-10" data-testid="submit-job-page">
      <div>
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#8A8A9A] mb-2">New Job</p>
        <h1 className="font-display text-3xl font-light tracking-tight text-[#1A1A2E]" data-testid="submit-job-heading">Submit a render</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Service Type */}
        <div>
          <label className="text-xs text-[#8A8A9A] block mb-3">Service</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {SERVICE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setServiceType(opt.value)}
                data-testid={`service-option-${opt.value}`}
                className={`p-3 rounded-lg text-left transition-colors ${
                  serviceType === opt.value
                    ? 'bg-[#FFF0EA] border-[#FF6B4A]/30'
                    : 'bg-[#FFFFFF] border-gray-100 hover:border-gray-300'
                } border`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <opt.icon className={`w-3.5 h-3.5 ${serviceType === opt.value ? 'text-[#FF6B4A]' : 'text-[#8A8A9A]'}`} />
                  <span className="text-xs font-medium text-[#1A1A2E]">{opt.label}</span>
                </div>
                <p className="text-[10px] text-[#8A8A9A] leading-relaxed line-clamp-2">{opt.desc}</p>
                <div className="font-mono text-[10px] text-[#FF6B4A] mt-1.5">{opt.cost} cr</div>
              </button>
            ))}
          </div>
        </div>

        {/* Style Presets */}
        {showPresets && currentPresets.length > 0 && (
          <div data-testid="preset-section">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs text-[#8A8A9A]">
                Style Preset {selectedPreset && <span className="text-[#FF6B4A]">— {selectedPreset.name}</span>}
              </label>
              {selectedPreset && (
                <button type="button" onClick={() => setSelectedPreset(null)} className="text-[10px] text-[#8A8A9A] hover:text-[#FF6B4A]">Clear</button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${
                    activeCategory === cat ? 'bg-[#1A1A2E] text-white' : 'bg-gray-100 text-[#4A4A5A] hover:bg-gray-200'
                  }`}
                  data-testid={`preset-category-${cat}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 max-h-80 overflow-y-auto pr-1">
              {filteredPresets.map((preset) => {
                const isSelected = selectedPreset?.id === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => setSelectedPreset(isSelected ? null : preset)}
                    data-testid={`preset-${preset.id}`}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-[1.02] ${
                      isSelected ? 'border-[#FF6B4A] shadow-md' : 'border-transparent'
                    }`}
                    style={{ background: preset.thumb }}
                  >
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#FF6B4A] flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-1.5 text-left bg-gradient-to-t from-black/70 to-transparent">
                      <div className="text-[10px] font-semibold text-white leading-tight">{preset.name}</div>
                      <div className="text-[8px] text-white/70">{preset.category}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Title */}
        <div>
          <label className="text-xs text-[#8A8A9A] block mb-2">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={serviceType === 'ai_video' ? 'A scenic mountain timelapse' : 'Product render for Q2 campaign'}
            className="w-full bg-[#FFFFFF] border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1A1A2E] placeholder:text-[#8A8A9A]/50 focus:outline-none focus:border-[#FF6B4A]/30 transition-colors"
            data-testid="job-title-input"
          />
        </div>

        {/* Description / Prompt */}
        <div>
          <label className="text-xs text-[#8A8A9A] block mb-2">
            {showPresets ? 'Prompt' : 'Description'}
            {selectedPreset && <span className="text-[#FF6B4A] ml-1">(preset style will be applied)</span>}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              serviceType === 'ai_video'
                ? 'A majestic eagle soaring over snow-capped mountains at sunrise...'
                : serviceType === 'ai_image'
                ? 'A serene Japanese garden with cherry blossoms in spring...'
                : 'What needs to be rendered...'
            }
            rows={3}
            className="w-full bg-[#FFFFFF] border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1A1A2E] placeholder:text-[#8A8A9A]/50 focus:outline-none focus:border-[#FF6B4A]/30 transition-colors resize-none"
            data-testid="job-description-input"
          />
        </div>

        {/* File Upload */}
        {!showPresets && (
          <div>
            <label className="text-xs text-[#8A8A9A] block mb-2">File (optional)</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragOver ? 'border-[#FF6B4A]/40 bg-[#FF6B4A]/[0.02]' : 'border-white/[0.08] hover:border-gray-300'
              }`}
              onClick={() => document.getElementById('file-input').click()}
              data-testid="file-upload-zone"
            >
              <input id="file-input" type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="file-input" />
              <Upload className="w-5 h-5 text-[#8A8A9A] mx-auto mb-2" />
              {file ? (
                <div>
                  <p className="text-xs text-[#1A1A2E]">{file.name}</p>
                  <p className="text-[10px] text-[#8A8A9A] mt-0.5">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              ) : (
                <p className="text-xs text-[#8A8A9A]">Drop files or click to browse</p>
              )}
            </div>
          </div>
        )}

        {/* Cost */}
        {selectedService && (
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-100" data-testid="cost-summary">
            <span className="text-xs text-[#8A8A9A]">Estimated cost</span>
            <span className="font-mono text-sm text-[#FF6B4A]">{selectedService.cost} credits</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={loading || !serviceType || !title}
          className="w-full bg-[#FF6B4A] text-white font-medium py-5 rounded-md hover:bg-[#FF5533] disabled:opacity-30 text-sm"
          data-testid="submit-job-button"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4 mr-2" /> Submit</>}
        </Button>
      </form>
    </div>
  );
}
